import { Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { Kapselvarsel } from './components/Kapselvarsel';
import { OffentligHjelp } from './components/OffentligHjelp';
import { LoadingBlock } from './components/ui';
import { AuthProvider } from './context/AuthContext';
import { STARTKAPITTEL, veiledningssti } from './lib/hjelpesider';
import { lastDel } from './lib/lastDel';
import { AngrerettPage } from './pages/AngrerettPage';
import { BekreftEpostPage } from './pages/BekreftEpostPage';
import { BliMedPage } from './pages/BliMedPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { GjesteinformasjonPage } from './pages/GjesteinformasjonPage';
import { InvitasjonForhandsvisning } from './pages/InvitasjonForhandsvisning';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { OppforingPage } from './pages/OppforingPage';
import { PaymentReturnPage } from './pages/PaymentReturnPage';
import { PrisPage } from './pages/PrisPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { RegisterPage } from './pages/RegisterPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { RsvpPage } from './pages/RsvpPage';
import { RsvpRettigheterPage } from './pages/RsvpRettigheterPage';
import { SalgsbetingelserPage } from './pages/SalgsbetingelserPage';
import { VelgBryllupPage } from './pages/VelgBryllupPage';
import { VilkaarPage } from './pages/VilkaarPage';

/*
  Delene som lastes først når de trengs. Se `lib/lastDel.ts`.

  Appen og portalen er mesteparten av koden og ligger bak innlogging; den som
  ser forsiden, prislista eller et kapittel i veiledningen, skal ikke hente
  dem. Hjelpesidene er for sin del mest tekst – to tusen linjer med den – og
  det er heller ingen grunn til at forsiden skal bære dem.
*/
const AppRuter = lastDel(() => import('./AppRuter'));
const AdminRuter = lastDel(() => import('./AdminRuter'));
const BrukerveiledningPage = lastDel(() =>
  import('./pages/BrukerveiledningPage').then((m) => ({ default: m.BrukerveiledningPage })),
);
const VanligeSporsmalPage = lastDel(() =>
  import('./pages/VanligeSporsmalPage').then((m) => ({ default: m.VanligeSporsmalPage })),
);

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/* Utenfor rutene: beskjeden gjelder hele siden, også forsida og
            svarlenkene gjestene får. */}
        <Kapselvarsel />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          {/* Egen adresse, ikke et anker på forsiden: prisen er det folk
              deler videre og søker etter. */}
          <Route path="/priser" element={<PrisPage />} />
          <Route path="/logg-inn" element={<LoginPage />} />
          <Route path="/registrer" element={<RegisterPage />} />
          <Route path="/glemt-passord" element={<ForgotPasswordPage />} />
          <Route path="/nytt-passord" element={<ResetPasswordPage />} />
          {/* Lenken i velkomstmailen. Utenfor /app: den åpnes like gjerne på
              en telefon der man ikke er logget inn. */}
          <Route path="/bekreft-epost" element={<BekreftEpostPage />} />
          {/* Lenken den andre i brudeparet får. Utenfor /app: den som åpner
              den, har som regel ingen konto ennå. */}
          <Route path="/bli-med" element={<BliMedPage />} />
          {/* Lenken et lokale får for å fylle ut sin egen oppføring. Uten konto. */}
          <Route path="/oppforing/:token" element={<OppforingPage />} />
          <Route path="/kom-i-gang" element={<OnboardingPage />} />
          {/* Utenfor /app: valget tas før man er inne i et bestemt bryllup. */}
          <Route path="/velg-bryllup" element={<VelgBryllupPage />} />
          <Route path="/personvern" element={<PrivacyPage />} />
          {/* Standardteksten brudeparet kan vise gjestene sine. Åpen, og med
              vilje generisk: hvem som gifter seg med hvem, legger vi ikke ut. */}
          <Route path="/gjesteinformasjon" element={<GjesteinformasjonPage />} />
          <Route path="/vilkaar" element={<VilkaarPage />} />
          <Route path="/vilkaar/angrerett" element={<AngrerettPage />} />
          {/*
            Adressen med én t. Den sto i den første beskjeden om hvor skjemaet
            skulle ligge, og kan ha blitt delt videre. En lenke til angreretten
            som ender i «siden finnes ikke», er den dyreste 404-en vi har.
          */}
          <Route path="/vilkaar/angreret" element={<Navigate to="/vilkaar/angrerett" replace />} />
          <Route path="/salgsbetingelser" element={<SalgsbetingelserPage />} />
          <Route path="/rsvp/:token" element={<RsvpPage />} />
          {/* Gjestens egne rettigheter, på gjestens egen lenke. «slett» er der
              bekreftelseslenka fra e-posten lander; begge er den samme siden,
              fordi det er den samme saken. */}
          <Route path="/rsvp/:token/personvern" element={<RsvpRettigheterPage />} />
          <Route path="/rsvp/:token/slett" element={<RsvpRettigheterPage />} />
          {/* Utenfor /app: forhåndsvisningen skal se ut som gjestesiden, ikke
              ligge inni menyen til brudeparet. */}
          <Route path="/invitasjon" element={<InvitasjonForhandsvisning />} />
          <Route path="/betaling/retur" element={<PaymentReturnPage />} />

          {/*
            Hjelpesidene, åpent for alle. Den samme teksten som under /app/hjelp
            og /app/sporsmal, men i nettstedets ramme og uten innlogging – for
            den som vurderer tjenesten, og for søkemotorene, som ikke kommer
            forbi et innloggingsskjema. Serveren gir hvert kapittel sitt eget
            hode og fører dem opp i nettstedskartet.

            /hjelp uten kapittel sendes til det første. Én adresse per tekst:
            serveren gjør det samme med 301 for den som kommer rett dit.
          */}
          <Route
            path="/hjelp"
            element={<Navigate to={veiledningssti(STARTKAPITTEL, 'offentlig')} replace />}
          />
          <Route
            path="/hjelp/:kapittel"
            element={
              <OffentligHjelp>
                <Suspense fallback={<LoadingBlock />}>
                  <BrukerveiledningPage />
                </Suspense>
              </OffentligHjelp>
            }
          />
          <Route
            path="/sporsmal"
            element={
              <OffentligHjelp>
                <Suspense fallback={<LoadingBlock />}>
                  <VanligeSporsmalPage />
                </Suspense>
              </OffentligHjelp>
            }
          />

          {/* Planleggingen og portalen, hver i sin egen fil. Rutene under dem
              står i AppRuter.tsx og AdminRuter.tsx. */}
          <Route
            path="/app/*"
            element={
              <Suspense fallback={<LoadingBlock label="Henter planleggingen …" />}>
                <AppRuter />
              </Suspense>
            }
          />
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={<LoadingBlock />}>
                <AdminRuter />
              </Suspense>
            }
          />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
